import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { InsertDriveFileOutlined as InsertDriveFileIcon } from "@mui/icons-material";
import { api } from "@accredo/fe-shared/services/dataService.js";

/**
 * Small preview thumbnail for a claim attachment (Anlage).
 *
 * Fetches the backend's small "preview" conversion via the authenticated
 * axios client and renders it as an object URL — a plain <img src={previewUrl}>
 * would not carry the Bearer auth token. Falls back to a generic file icon
 * when no preview conversion exists (previewUrl is null) or the fetch fails.
 */
export default function AttachmentThumbnail({ previewUrl, size = 40 }) {
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (!previewUrl) {
      setObjectUrl(null);
      return;
    }

    let currentObjectUrl = null;
    let cancelled = false;

    api
      .get(previewUrl, { responseType: "blob" })
      .then((response) => {
        if (cancelled) return;
        currentObjectUrl = URL.createObjectURL(response.data);
        setObjectUrl(currentObjectUrl);
      })
      .catch(() => {
        if (!cancelled) setObjectUrl(null);
      });

    return () => {
      cancelled = true;
      if (currentObjectUrl) URL.revokeObjectURL(currentObjectUrl);
    };
  }, [previewUrl]);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "action.hover",
        flexShrink: 0,
      }}
    >
      {objectUrl ? (
        <Box
          component="img"
          src={objectUrl}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <InsertDriveFileIcon
          sx={{ fontSize: size * 0.6, color: "text.disabled" }}
        />
      )}
    </Box>
  );
}

AttachmentThumbnail.propTypes = {
  previewUrl: PropTypes.string,
  size: PropTypes.number,
};
